import { useUnit } from 'effector-react';
import { Bell } from 'lucide-react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  $alerts,
  $filter,
  AlertIcon,
  AlertMessage,
  alertsRefreshed,
  dismissAlertFx,
  filterChanged,
  type Alert,
} from '../../entities/alert';
import { $positions, positionSelected } from '../../entities/position';
import { closeDialogOpened } from '../../features/close-position';
import { fillDialogOpened } from '../../features/record-fill';
import { formatDateTime, formatRelative } from '../../shared/lib/format';
import { useLocale } from '../../shared/lib/i18n/use-locale';
import { routes } from '../../shared/routing';
import { Button, EmptyState, Pill, Segmented, Spinner, StatusDot, Tooltip } from '../../shared/ui';
import { AppShell } from '../../widgets/app-shell';

export function AlertsPage() {
  const { t } = useTranslation();
  const [alerts, filter, setFilter, refresh] = useUnit([
    $alerts,
    $filter,
    filterChanged,
    alertsRefreshed,
  ]);
  useEffect(() => {
    refresh();
  }, [refresh]);
  return (
    <AppShell page="alerts" crumbs={[t('nav.alerts')]}>
      <div className="mx-auto w-full max-w-5xl space-y-4 px-6 py-6 max-sm:px-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="text-lg font-semibold">{t('nav.alerts')}</h1>
          <Segmented
            label={t('alerts.filter')}
            value={filter}
            onValueChange={setFilter}
            options={[
              { value: 'open', label: t('alerts.open') },
              { value: 'all', label: t('alerts.all') },
            ]}
          />
        </div>
        {alerts === null ? (
          <div className="flex justify-center py-16">
            <Spinner label={t('common.loading')} />
          </div>
        ) : alerts.length === 0 ? (
          <EmptyState
            className="py-16"
            icon={<Bell />}
            title={t(filter === 'open' ? 'alerts.emptyOpen' : 'alerts.emptyAll')}
            body={t('alerts.emptyBody')}
          />
        ) : (
          <ul className="divide-y divide-border-subtle overflow-hidden rounded-lg border border-border-subtle bg-bg-1">
            {alerts.map((alert) => (
              <AlertRow key={alert.id} alert={alert} />
            ))}
          </ul>
        )}
      </div>
    </AppShell>
  );
}

const STATUS_DOT = { open: 'warning', done: 'good', dismissed: null, info: null } as const;

function AlertRow({ alert }: { alert: Alert }) {
  const { t } = useTranslation();
  const locale = useLocale();
  const [positions, dismiss, select] = useUnit([$positions, dismissAlertFx, positionSelected]);
  const position = positions?.find((p) => p.id === alert.position_id);
  const dot = STATUS_DOT[alert.status];
  const actionable = alert.status === 'open' && position !== undefined;
  return (
    <li className="group flex min-h-9 flex-wrap items-center gap-x-3 gap-y-1 px-4 py-1.5 hover:bg-bg-3">
      {dot ? (
        <StatusDot status={dot} label={t(`alerts.statuses.${alert.status}`)} />
      ) : (
        <span className="size-1.5" />
      )}
      <AlertIcon alert={alert} />
      <span className="min-w-0 flex-1 text-sm text-text-1">
        <AlertMessage alert={alert} />
      </span>
      {alert.position_id !== null && (
        <button
          type="button"
          className="rounded-sm text-xs text-text-3 outline-none hover:text-text-1 focus-visible:ring-2 focus-visible:ring-focus"
          onClick={() => {
            select(alert.position_id);
            void routes.position.open({ id: String(alert.position_id) });
          }}
        >
          {t('alerts.positionLink', { symbol: alert.symbol ?? '', id: alert.position_id })}
        </button>
      )}
      {alert.status !== 'open' && alert.status !== 'info' && (
        <Pill>{t(`alerts.statuses.${alert.status}`)}</Pill>
      )}
      <Tooltip content={formatDateTime(alert.created_at, locale)}>
        <span className="text-xs text-text-3">
          {formatRelative(alert.created_at, new Date(), locale)}
        </span>
      </Tooltip>
      {actionable && alert.kind === 'level_hit' && alert.level_n !== null && (
        <Button
          size="sm"
          onClick={() => {
            fillDialogOpened({ position, level: alert.level_n ?? 1 });
          }}
        >
          {t('alerts.recordFill')}
        </Button>
      )}
      {actionable && alert.kind === 'target_hit' && (
        <Button
          size="sm"
          onClick={() => {
            closeDialogOpened(position);
          }}
        >
          {t('alerts.recordSale')}
        </Button>
      )}
      {alert.status === 'open' && (
        <Button
          size="sm"
          variant="ghost"
          onClick={() => {
            void dismiss(alert.id).catch(() => undefined);
          }}
        >
          {t('alerts.dismiss')}
        </Button>
      )}
    </li>
  );
}
